import { FullScreenSpinner } from "@custos/ui";
import { useEffect, useRef, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { AdminShell, NAV, PageHeader } from "./components/AdminShell";
import { api } from "./lib/api";
import { useAuth } from "./lib/auth";
import { useCan } from "./lib/permissions";
import { useAdminMe } from "./lib/queries";
import { AccountPage, ForcedPasswordPage } from "./pages/Account";
import { MessagesPage } from "./pages/Messages";
import { ReportsPage } from "./pages/Reports";
import { StaffPage } from "./pages/Staff";
import { SupportPage } from "./pages/Support";
import { AuditPage } from "./pages/Audit";
import { LoginPage, TwoFactorPage } from "./pages/auth";
import { LimitsPage } from "./pages/Limits";
import { SwapsPage } from "./pages/Swaps";
import { OverviewPage } from "./pages/Overview";
import { DepositsPage, WithdrawalsPage } from "./pages/Queues";
import { UserDetailPage, UsersPage } from "./pages/Users";
import { WalletsPage } from "./pages/Wallets";
import { VaultRecoveryPage } from "./pages/VaultRecovery";

/** Resume the admin session from the httpOnly cookie after a reload. */
function useRestoreSession() {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api
      .restoreSession()
      .then((ok) => {
        if (!ok) useAuth.getState().setAnonymous();
      })
      .catch(() => {
        useAuth.getState().setAnonymous();
        useAuth
          .getState()
          .setNotice("Can't reach the server right now. Check that the API is running, then reload.");
      });
  }, []);
}

function RequireAdmin() {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === "loading") return <FullScreenSpinner />;
  if (status === "anonymous")
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <SignedIn />;
}

/** Waits for the role; a temporary password shows only the "choose your password" screen (D77). */
function SignedIn() {
  const me = useAdminMe();
  if (me.isPending) return <FullScreenSpinner />;
  if (me.data?.mustChangePassword) return <ForcedPasswordPage />;
  return (
    <AdminShell>
      <Outlet />
    </AdminShell>
  );
}

/** A page the role can open, or a plain "no access" note (the API refuses anyway). */
function Allowed({ path, children }: { path: string; children: ReactNode }) {
  const can = useCan();
  const perms = NAV.find((n) => n.to === path)?.perms ?? [];
  if (perms.length === 0 || can(...perms)) return children;
  return (
    <PageHeader
      title="No access"
      subtitle="Your role doesn't include this page. Ask the Owner if you need it."
    />
  );
}

/** Home: the overview, or the first page this role may open. */
function Home() {
  const can = useCan();
  if (can("dashboard.view")) return <OverviewPage />;
  const first = NAV.find((n) => n.perms.length > 0 && can(...n.perms)) ?? NAV[NAV.length - 1]!;
  return <Navigate to={first.to} replace />;
}

function GuestOnly({ children }: { children?: ReactNode }) {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === "loading") return <FullScreenSpinner />;
  if (status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from.startsWith("/") ? from : "/"} replace />;
  }
  return children ?? <Outlet />;
}

export function App() {
  useRestoreSession();
  return (
    <Routes>
      <Route element={<GuestOnly />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<TwoFactorPage />} />
      </Route>
      <Route element={<RequireAdmin />}>
        <Route index element={<Home />} />
        <Route
          path="/withdrawals"
          element={
            <Allowed path="/withdrawals">
              <WithdrawalsPage />
            </Allowed>
          }
        />
        <Route
          path="/deposits"
          element={
            <Allowed path="/deposits">
              <DepositsPage />
            </Allowed>
          }
        />
        <Route
          path="/users"
          element={
            <Allowed path="/users">
              <UsersPage />
            </Allowed>
          }
        />
        <Route
          path="/users/:id"
          element={
            <Allowed path="/users">
              <UserDetailPage />
            </Allowed>
          }
        />
        <Route
          path="/wallets"
          element={
            <Allowed path="/wallets">
              <WalletsPage />
            </Allowed>
          }
        />
        <Route
          path="/swaps"
          element={
            <Allowed path="/swaps">
              <SwapsPage />
            </Allowed>
          }
        />
        <Route
          path="/vault-recovery"
          element={
            <Allowed path="/vault-recovery">
              <VaultRecoveryPage />
            </Allowed>
          }
        />
        <Route
          path="/limits"
          element={
            <Allowed path="/limits">
              <LimitsPage />
            </Allowed>
          }
        />
        <Route
          path="/audit"
          element={
            <Allowed path="/audit">
              <AuditPage />
            </Allowed>
          }
        />
        <Route
          path="/staff"
          element={
            <Allowed path="/staff">
              <StaffPage />
            </Allowed>
          }
        />
        <Route
          path="/messages"
          element={
            <Allowed path="/messages">
              <MessagesPage />
            </Allowed>
          }
        />
        <Route
          path="/support"
          element={
            <Allowed path="/support">
              <SupportPage />
            </Allowed>
          }
        />
        <Route
          path="/support/:id"
          element={
            <Allowed path="/support">
              <SupportPage />
            </Allowed>
          }
        />
        <Route
          path="/reports"
          element={
            <Allowed path="/reports">
              <ReportsPage />
            </Allowed>
          }
        />
        <Route path="/account" element={<AccountPage />} />
        <Route path="*" element={<PageHeader title="Page not found" />} />
      </Route>
    </Routes>
  );
}
